import React, { useMemo, useState } from "react";

/* ──────────────────────────────────────────────
   Savings calculator — model & sources
   All card rates are published standard pricing,
   verified July 2026 (see SOURCES below). The
   pay-by-bank side is an indicative market band —
   exact Paybank2bank pricing comes from sales.
   ────────────────────────────────────────────── */

/* Indicative pay-by-bank cost band.
   Real pricing floors at 35–50p per transaction for
   low-risk profiles; higher-risk profiles are priced
   ad valorem (0.2–0.5%), so each band takes the
   greater of its floor and its percentage cost. High
   end tracks Stripe's published Pay by bank rate
   (0.5% + 20p, capped at £5) so even the conservative
   estimate is market-verifiable. */
const PBB_BAND = {
  low: { pct: 0.2, fixed: 0, floor: 0.35, cap: 3 },
  high: { pct: 0.5, fixed: 0.2, floor: 0.5, cap: 5 },
};

const PROVIDERS = {
  GBP: [
    { id: "stripe", label: "Stripe", pct: 1.5, fixed: 0.2, note: "1.5% + 20p — UK consumer cards, standard pricing" },
    { id: "paypal", label: "PayPal", pct: 2.9, fixed: 0.3, note: "2.9% + 30p — standard online transactions" },
    { id: "square", label: "Square", pct: 1.4, fixed: 0.25, note: "1.4% + 25p — online, UK cards" },
    { id: "adyen", label: "Adyen / Interchange++", pct: 0.9, fixed: 0.11, note: "≈0.3% interchange + scheme fees + 0.6% markup + 11p" },
    { id: "custom", label: "I know my own rate", pct: 1.5, fixed: 0.2, note: "Enter the blended rate from your acquirer statement" },
  ],
  EUR: [
    { id: "stripe", label: "Stripe", pct: 1.5, fixed: 0.25, note: "1.5% + €0.25 — EEA consumer cards, standard pricing" },
    { id: "paypal", label: "PayPal", pct: 2.99, fixed: 0.39, note: "2.99% + €0.39 — standard online transactions" },
    { id: "adyen", label: "Adyen / Interchange++", pct: 0.9, fixed: 0.11, note: "≈0.3% interchange + scheme fees + 0.6% markup + €0.11" },
    { id: "custom", label: "I know my own rate", pct: 1.5, fixed: 0.25, note: "Enter the blended rate from your acquirer statement" },
  ],
};

const SOURCES = [
  { name: "Stripe UK — standard pricing (1.5% + 20p UK cards, 2.5% + 20p EEA, 3.25% + 20p international, £20 disputes)", url: "https://stripe.com/gb/pricing" },
  { name: "Stripe UK — Pay by bank pricing (0.5% + 20p, capped at £5)", url: "https://stripe.com/gb/pricing/local-payment-methods" },
  { name: "PayPal UK — merchant fees (2.9% + 30p online, £14 chargebacks)", url: "https://www.paypal.com/uk/webapps/mpp/merchant-fees" },
  { name: "Square UK — pricing (1.4% + 25p online, UK cards)", url: "https://squareup.com/gb/en/pricing" },
  { name: "Adyen — pricing (11p + interchange++ + 0.60% markup)", url: "https://www.adyen.com/en_GB/pricing" },
  { name: "PSR — card scheme & processing fees market review, Mar 2025 (fees up 25%+ in real terms since 2017)", url: "https://www.psr.org.uk/publications/market-reviews/mr22110-market-review-of-card-scheme-and-processing-fees-final-report/" },
  { name: "PSR — cross-border interchange market review, Dec 2024 (UK–EEA online interchange raised to 1.15% / 1.50%)", url: "https://www.psr.org.uk/our-work/market-reviews/market-review-into-cross-border-interchange-fees/" },
  { name: "British Retail Consortium — Payments Survey 2025 (£1.48bn card fees, more than double 2019)", url: "https://brc.org.uk/market-intelligence/publications/benchmarks/payments-survey/" },
  { name: "UK Finance — Annual Fraud Report 2025 (£572.6m card fraud losses in 2024)", url: "https://www.ukfinance.org.uk/news-and-insight/press-release/fraud-report-2025-press-release" },
];

const clampNum = (v, min, max) => Math.min(max, Math.max(min, v));

/* Parse a user-typed amount like "1,250,000" back to a number */
const parseAmount = (s) => clampNum(Number(String(s).replace(/[^0-9]/g, "")) || 0, 0, 1000000000);
const fmtInput = (n) => (n ? n.toLocaleString("en-GB") : "");

const fmtMoney = (value, currency, digits = 0) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

/* Volume slider works on a log scale: 5k → 5M / month */
const VOL_MIN_EXP = Math.log10(5000);
const VOL_MAX_EXP = Math.log10(5000000);
const sliderToVolume = (t) => {
  const raw = Math.pow(10, VOL_MIN_EXP + (t / 1000) * (VOL_MAX_EXP - VOL_MIN_EXP));
  // Round to 2 significant figures so the slider lands on friendly numbers
  const mag = Math.pow(10, Math.floor(Math.log10(raw)) - 1);
  return Math.round(raw / mag) * mag;
};
const volumeToSlider = (v) =>
  Math.round(
    ((Math.log10(clampNum(v, 5000, 5000000)) - VOL_MIN_EXP) / (VOL_MAX_EXP - VOL_MIN_EXP)) * 1000
  );

function perTxCost(atv, band) {
  return Math.min(Math.max((atv * band.pct) / 100 + band.fixed, band.floor), band.cap);
}

export function computeSavings({ monthlyVolume, atv, pct, fixed }) {
  const safeAtv = Math.max(1, atv || 1);
  const txCount = monthlyVolume / safeAtv;
  const cardMonthly = (monthlyVolume * pct) / 100 + txCount * fixed;
  const pbbLowMonthly = txCount * perTxCost(safeAtv, PBB_BAND.low);
  const pbbHighMonthly = txCount * perTxCost(safeAtv, PBB_BAND.high);
  const cardAnnual = cardMonthly * 12;
  const pbbAnnualLow = pbbLowMonthly * 12;
  const pbbAnnualHigh = pbbHighMonthly * 12;
  return {
    txCount,
    cardAnnual,
    cardEffPct: monthlyVolume > 0 ? (cardMonthly / monthlyVolume) * 100 : 0,
    pbbAnnualLow,
    pbbAnnualHigh,
    saveMin: cardAnnual - pbbAnnualHigh,
    saveMax: cardAnnual - pbbAnnualLow,
  };
}

function useCalcState(initialCurrency = "GBP") {
  const [currency, setCurrency] = useState(initialCurrency);
  const [monthlyVolume, setMonthlyVolume] = useState(100000);
  const [atv, setAtv] = useState(80);
  const [providerId, setProviderId] = useState("stripe");
  const [customPct, setCustomPct] = useState(1.5);
  const [customFixed, setCustomFixed] = useState(0.2);

  const providers = PROVIDERS[currency];
  const provider = providers.find((p) => p.id === providerId) || providers[0];
  const pct = provider.id === "custom" ? customPct : provider.pct;
  const fixed = provider.id === "custom" ? customFixed : provider.fixed;

  const results = useMemo(
    () => computeSavings({ monthlyVolume, atv, pct, fixed }),
    [monthlyVolume, atv, pct, fixed]
  );

  const changeCurrency = (next) => {
    setCurrency(next);
    if (!PROVIDERS[next].some((p) => p.id === providerId)) setProviderId("stripe");
  };

  return {
    currency, changeCurrency,
    monthlyVolume, setMonthlyVolume,
    atv, setAtv,
    providers, provider, setProviderId,
    customPct, setCustomPct,
    customFixed, setCustomFixed,
    pct, fixed, results,
  };
}

function buildSummary(state) {
  const { currency, monthlyVolume, atv, provider, pct, fixed, results } = state;
  const sym = currency === "GBP" ? "£" : "€";
  return [
    "— Savings calculator estimate —",
    `Monthly card volume: ${fmtMoney(monthlyVolume, currency)}`,
    `Average transaction: ${fmtMoney(atv, currency)}`,
    `Current provider: ${provider.label} (${pct}% + ${sym}${fixed.toFixed(2)})`,
    `Estimated annual card cost: ${fmtMoney(results.cardAnnual, currency)}`,
    `Estimated annual saving with pay-by-bank: ${fmtMoney(Math.max(0, results.saveMin), currency)} – ${fmtMoney(Math.max(0, results.saveMax), currency)}`,
    "",
    "Please send me exact pricing for my volumes.",
  ].join("\n");
}

function SavingsResultPanel({ state, onTalkToSales, compact }) {
  const { currency, results } = state;
  const { cardAnnual, pbbAnnualLow, pbbAnnualHigh, saveMin, saveMax, cardEffPct } = results;
  const noSavings = saveMax <= 0;
  const pctMin = cardAnnual > 0 ? Math.max(0, (saveMin / cardAnnual) * 100) : 0;
  const pctMax = cardAnnual > 0 ? Math.max(0, (saveMax / cardAnnual) * 100) : 0;
  const barPbb = cardAnnual > 0 ? clampNum((pbbAnnualHigh / cardAnnual) * 100, 2, 100) : 0;

  return (
    <div className={`calcResult ${compact ? "calcResultCompact" : ""}`}>
      {noSavings ? (
        <>
          <div className="calcResultLabel">Your estimated saving</div>
          <div className="calcSaveValue">Let&rsquo;s talk</div>
          <p className="calcResultSub">
            Your current rate is already low — but card mix, EEA/commercial
            card surcharges and chargebacks usually push real costs above the
            headline rate. Our team can price your exact flows.
          </p>
        </>
      ) : (
        <>
          <div className="calcResultLabel">Estimated annual saving</div>
          <div className="calcSaveValue">
            {fmtMoney(Math.max(0, saveMin), currency)}
            <span className="calcSaveDash">&ndash;</span>
            {fmtMoney(saveMax, currency)}
          </div>
          <div className="calcSavePct">
            {Math.round(pctMin)}&ndash;{Math.round(pctMax)}% off your card processing bill
          </div>

          <div className="calcBars" aria-hidden="true">
            <div className="calcBarRow">
              <span className="calcBarLabel">Cards today</span>
              <div className="calcBarTrack">
                <div className="calcBar calcBarCards" style={{ width: "100%" }} />
              </div>
              <span className="calcBarValue">{fmtMoney(cardAnnual, currency)}/yr</span>
            </div>
            <div className="calcBarRow">
              <span className="calcBarLabel">Pay by bank</span>
              <div className="calcBarTrack">
                <div className="calcBar calcBarPbb" style={{ width: `${barPbb}%` }} />
              </div>
              <span className="calcBarValue">
                {fmtMoney(pbbAnnualLow, currency)}&ndash;{fmtMoney(pbbAnnualHigh, currency)}/yr
              </span>
            </div>
          </div>

          {!compact && (
            <div className="calcEffRate">
              Your effective card rate: <strong>{cardEffPct.toFixed(2)}%</strong> of turnover
              &nbsp;&middot;&nbsp; pay-by-bank: <strong>from 35&ndash;50p per transaction</strong>
            </div>
          )}
        </>
      )}

      <button
        className="btn btnAccent btnGlow btnLg btnFull"
        onClick={() => onTalkToSales(buildSummary(state))}
      >
        Get my exact quote
        <span className="btnArrow">&rarr;</span>
      </button>
      <div className="calcCtaHint">
        Free, no commitment — we reply within one business day.
      </div>
    </div>
  );
}

function CalcInputs({ state, compact }) {
  const {
    currency, changeCurrency,
    monthlyVolume, setMonthlyVolume,
    atv, setAtv,
    providers, provider, setProviderId,
    customPct, setCustomPct,
    customFixed, setCustomFixed,
  } = state;

  const sym = currency === "GBP" ? "£" : "€";

  return (
    <div className="calcInputs">
      <div className="calcFieldRow">
        <label className="field calcFieldGrow">
          <span className="fieldLabel">Monthly card turnover</span>
          <div className="inputAffix">
            <span className="inputAffixSym" aria-hidden="true">{sym}</span>
            <input
              className="input inputAffixField"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="100,000"
              value={fmtInput(monthlyVolume)}
              onChange={(e) => setMonthlyVolume(parseAmount(e.target.value))}
            />
          </div>
          <span className="fieldHint">
            The total value of card payments you take in a typical month, before fees.
          </span>
        </label>
        <div className="field calcCurrencyField">
          <span className="fieldLabel">Currency</span>
          <div className="calcCurrencyToggle" role="group" aria-label="Currency">
            {["GBP", "EUR"].map((c) => (
              <button
                key={c}
                type="button"
                className={`calcCurrencyBtn ${currency === c ? "calcCurrencyBtnActive" : ""}`}
                onClick={() => changeCurrency(c)}
              >
                {c === "GBP" ? "£ GBP" : "€ EUR"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <input
        className="calcSlider"
        type="range"
        min="0"
        max="1000"
        value={volumeToSlider(monthlyVolume)}
        onChange={(e) => setMonthlyVolume(sliderToVolume(Number(e.target.value)))}
        aria-label="Monthly card turnover slider"
      />
      <div className="calcSliderMarks" aria-hidden="true">
        <span>{currency === "GBP" ? "£5k" : "€5k"}</span>
        <span>{currency === "GBP" ? "£100k" : "€100k"}</span>
        <span>{currency === "GBP" ? "£5m" : "€5m"}</span>
      </div>

      <div className="calcFieldRow">
        <label className="field">
          <span className="fieldLabel">Average transaction</span>
          <div className="inputAffix">
            <span className="inputAffixSym" aria-hidden="true">{sym}</span>
            <input
              className="input inputAffixField"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="80"
              value={fmtInput(atv)}
              onChange={(e) => setAtv(parseAmount(e.target.value))}
            />
          </div>
          <span className="fieldHint">Your typical order or payment size.</span>
        </label>
        <label className="field calcFieldGrow">
          <span className="fieldLabel">Current payment provider</span>
          <select
            className="input select"
            value={provider.id}
            onChange={(e) => setProviderId(e.target.value)}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </label>
      </div>

      {provider.id === "custom" ? (
        <div className="calcFieldRow">
          <label className="field">
            <span className="fieldLabel">Your rate (%)</span>
            <input
              className="input"
              type="number"
              min="0"
              max="10"
              step="0.05"
              value={customPct}
              onChange={(e) => setCustomPct(clampNum(Number(e.target.value) || 0, 0, 10))}
            />
          </label>
          <label className="field">
            <span className="fieldLabel">Fixed fee per transaction</span>
            <input
              className="input"
              type="number"
              min="0"
              max="5"
              step="0.01"
              value={customFixed}
              onChange={(e) => setCustomFixed(clampNum(Number(e.target.value) || 0, 0, 5))}
            />
          </label>
        </div>
      ) : (
        !compact && <div className="calcProviderNote">{provider.note}</div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Homepage teaser — compact widget linking to the
   full calculator page
   ────────────────────────────────────────────── */
export function SavingsTeaser({ onOpenCalculator, onTalkToSales }) {
  const state = useCalcState("GBP");

  return (
    <div className="calcTeaserCard">
      <div className="calcTeaserInputs">
        <CalcInputs state={state} compact />
        <button className="btn btnSecondary calcTeaserMore" onClick={onOpenCalculator}>
          Open the full calculator
          <span className="btnArrow">&rarr;</span>
        </button>
      </div>
      <SavingsResultPanel state={state} onTalkToSales={onTalkToSales} compact />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Full calculator page
   ────────────────────────────────────────────── */
export function SavingsPage({ onTalkToSales }) {
  const state = useCalcState("GBP");

  return (
    <div className="calcPage">
      <section className="section calcHero">
        <div className="container">
          <div className="sectionHeader reveal revealed">
            <div className="sectionLabel">Savings calculator</div>
            <h1 className="h2">
              How much are cards <span className="gradientText">really costing you</span>?
            </h1>
            <p className="sectionSub">
              Compare your current card processing fees with pay-by-bank.
              Estimates use published provider pricing and independent
              regulator data — sources are listed below.
            </p>
          </div>

          <div className="calcCard">
            <CalcInputs state={state} />
            <SavingsResultPanel state={state} onTalkToSales={onTalkToSales} />
          </div>

          <p className="calcDisclaimer">
            Estimates are indicative, based on published standard pricing for the
            selected provider (verified July 2026) and an indicative pay-by-bank
            cost of 35&ndash;50p per transaction for low-risk profiles &mdash; percentage-based
            (0.2&ndash;0.5%, capped per transaction) where transaction values or risk
            profiles are higher. Card estimates use
            domestic consumer card rates &mdash; real card bills are typically higher
            once premium, commercial and cross-border cards are included. This is
            not an offer or a quote; exact Paybank2bank pricing is confirmed by
            our sales team for your volumes and markets.
          </p>
        </div>
      </section>

      <section className="section sectionDark">
        <div className="container">
          <div className="sectionHeader reveal revealed">
            <div className="sectionLabelLight">Why cards cost more every year</div>
            <h2 className="h2Light">The numbers behind the estimate</h2>
            <p className="sectionSubLight">
              Independent regulators and industry bodies have documented how card
              acceptance costs keep climbing — while pay-by-bank moves money
              directly between accounts, skipping the card networks entirely.
            </p>
          </div>

          <div className="calcFactsGrid reveal revealed">
            {[
              {
                stat: "+25%",
                title: "Scheme fees up in real terms",
                desc: "The UK Payment Systems Regulator found Mastercard and Visa raised core scheme and processing fees by over 25% in real terms since 2017 — costing UK businesses at least £170m extra per year.",
              },
              {
                stat: "£1.48bn",
                title: "UK retailers' annual card bill",
                desc: "The British Retail Consortium's 2025 Payments Survey put retailers' card fees at £1.48bn — more than double what they paid in 2019.",
              },
              {
                stat: "5×",
                title: "Cross-border interchange hike",
                desc: "Post-Brexit, UK–EEA online card interchange jumped roughly fivefold to 1.15% (debit) and 1.50% (credit) — the PSR found no justification for the increase.",
              },
              {
                stat: "£572.6m",
                title: "Card fraud losses in 2024",
                desc: "UK Finance reported £572.6m lost to card fraud in 2024. Pay-by-bank payments are authenticated in the customer's own banking app, and there is no chargeback mechanism to administer.",
              },
            ].map((f) => (
              <div key={f.title} className="calcFactCard">
                <div className="calcFactStat">{f.stat}</div>
                <div className="calcFactTitle">{f.title}</div>
                <div className="calcFactDesc">{f.desc}</div>
              </div>
            ))}
          </div>

          <div className="calcExtras reveal revealed">
            <div className="calcExtrasTitle">Beyond the headline rate, cards also cost you:</div>
            <ul className="calcExtrasList">
              <li>Chargeback fees of &pound;14&ndash;&pound;20 per dispute (PayPal &pound;14, Stripe &pound;20) — pay-by-bank has no chargebacks.</li>
              <li>Premium, commercial and international cards billed at 1.9&ndash;3.25% + fees, well above headline rates.</li>
              <li>Settlement delays of 1&ndash;3 days — pay-by-bank settles in seconds, improving cash flow.</li>
              <li>PCI compliance and card-data handling overhead — no card numbers ever touch your systems.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="calcSources">
            <div className="calcSourcesTitle">Methodology &amp; sources</div>
            <p className="calcSourcesIntro">
              Card costs are modelled as <em>turnover &times; provider rate + transactions
              &times; fixed fee</em>, using each provider&rsquo;s published standard pricing for
              domestic consumer cards. The pay-by-bank band applies a per-transaction
              cost of 35&ndash;50p for low-risk profiles, switching to 0.2&ndash;0.5% ad valorem
              (with a per-transaction cap) where transaction values or risk profiles
              are higher &mdash; consistent with publicly listed open banking pricing.
              All sources verified July 2026:
            </p>
            <ul className="calcSourcesList">
              {SOURCES.map((s) => (
                <li key={s.url}>
                  <a className="legalLink" href={s.url} target="_blank" rel="noopener noreferrer">
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
